import React, { useState } from 'react';

// Define types for our component
interface XmlToJsonProps {}

// XML to JSON conversion function
const xmlToJson = (xml: string): any => {
  try {
    // Create a DOM parser and parse the XML string
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    // Helper function to convert XML nodes to JSON
    const convertNodeToJson = (node: Node): any => {
      // Text node - just return the text content
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue?.trim();
        return text === "" ? null : text;
      }
      
      // Element node - process as object
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const result: Record<string, any> = {};
        
        // Add attributes if present
        if (element.attributes && element.attributes.length > 0) {
          result["@attributes"] = {};
          for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];
            result["@attributes"][attr.nodeName] = attr.nodeValue;
          }
        }
        
        // Process child nodes
        let hasChildElements = false;
        const childNodes = element.childNodes;
        
        // Check if there are child elements
        for (let i = 0; i < childNodes.length; i++) {
          if (childNodes[i].nodeType === Node.ELEMENT_NODE) {
            hasChildElements = true;
            break;
          }
        }
        
        // If no child elements, process as simple node with potential text content
        if (!hasChildElements && childNodes.length <= 1) {
          const text = element.textContent?.trim();
          
          // If there are attributes, add text content as a special property
          if (result["@attributes"]) {
            result["#text"] = text === "" ? null : text;
            return result;
          } else {
            // No attributes and no children - return simple text value
            return text === "" ? null : text;
          }
        }
        
        // Process child elements
        for (let i = 0; i < childNodes.length; i++) {
          const child = childNodes[i];
          
          // Skip empty text nodes
          if (child.nodeType === Node.TEXT_NODE && child.nodeValue?.trim() === "") {
            continue;
          }
          
          // Process element node
          if (child.nodeType === Node.ELEMENT_NODE) {
            const childElement = child as Element;
            const nodeName = childElement.nodeName;
            
            // Convert child to JSON
            const childJson = convertNodeToJson(child);
            
            // Handle multiple children with same name
            if (result[nodeName] === undefined) {
              result[nodeName] = childJson;
            } else {
              // If this nodeName already exists, convert to an array
              if (!Array.isArray(result[nodeName])) {
                result[nodeName] = [result[nodeName]];
              }
              result[nodeName].push(childJson);
            }
          } else if (child.nodeType === Node.TEXT_NODE && child.nodeValue?.trim() !== "") {
            // Add non-empty text content
            result["#text"] = child.nodeValue?.trim();
          }
        }
        
        return result;
      }
      
      // Other node types - return null
      return null;
    };
    
    // Convert the root element
    const rootElement = xmlDoc.documentElement;
    const result: Record<string, any> = {};
    result[rootElement.nodeName] = convertNodeToJson(rootElement);
    
    return result;
  } catch (error) {
    throw error;
  }
};

// Main component to convert XML to JSON
const XmlToJson: React.FC<XmlToJsonProps> = () => {
  // State for the XML input
  const [xmlInput, setXmlInput] = useState<string>('');
  // State for the JSON output
  const [jsonOutput, setJsonOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('xml_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isJsonGenerated, setIsJsonGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for JSON formatting
  const [prettyPrint, setPrettyPrint] = useState<boolean>(true);
  // State for JSON indent level
  const [indentLevel, setIndentLevel] = useState<number>(2);
  // State for preserving attributes
  const [preserveAttributes, setPreserveAttributes] = useState<boolean>(true);
  // State for array handling
  const [arrayHandling, setArrayHandling] = useState<string>('auto');

  // Handle XML input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setXmlInput(e.target.value);
    setError('');
    setIsJsonGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<library>
  <book id="bk101" category="fiction">
    <title>The Great Gatsby</title>
    <author>F. Scott Fitzgerald</author>
    <year>1925</year>
    <price>9.99</price>
    <tags>
      <tag>classic</tag>
      <tag>american literature</tag>
      <tag>jazz age</tag>
    </tags>
    <available>true</available>
  </book>
  <book id="bk102" category="non-fiction">
    <title>A Brief History of Time</title>
    <author>Stephen Hawking</author>
    <year>1988</year>
    <price>12.50</price>
    <tags>
      <tag>science</tag>
      <tag>physics</tag>
      <tag>cosmology</tag>
    </tags>
    <available>false</available>
  </book>
  <book id="bk103" category="fiction">
    <title>1984</title>
    <author>George Orwell</author>
    <year>1949</year>
    <price>8.75</price>
    <tags>
      <tag>dystopian</tag>
      <tag>political</tag>
    </tags>
    <available>true</available>
  </book>
</library>`;

    setXmlInput(exampleXml);
    setError('');
    setIsJsonGenerated(false);
    setJsonOutput('');
  };

  // Format/Beautify XML
  const formatXml = () => {
    try {
      if (!xmlInput.trim()) {
        setError('Please enter XML data to format');
        return;
      }
      
      // Parse the XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlInput, "application/xml");
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName("parsererror");
      if (parseError.length > 0) {
        throw new Error("XML Parsing Error: " + parseError[0].textContent);
      }
      
      // Serialize back to string with pretty formatting
      const serializer = new XMLSerializer();
      let formattedXml = '';
      
      // Function to pretty print XML with indentation
      const prettyPrintXml = (node: Node, indent: string = ''): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue?.trim();
          return text ? indent + text + '\n' : '';
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          let result = indent + '<' + element.nodeName;
          
          // Add attributes
          for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];
            result += ` ${attr.nodeName}="${attr.nodeValue}"`;
          }
          
          // Check if there are any child nodes
          if (element.childNodes.length === 0) {
            result += '/>\n';
            return result;
          }
          
          result += '>\n';
          
          // Process child nodes
          for (let i = 0; i < element.childNodes.length; i++) {
            result += prettyPrintXml(element.childNodes[i], indent + '  ');
          }
          
          result += indent + '</' + element.nodeName + '>\n';
          return result;
        }
        
        return '';
      };
      
      // Add XML declaration
      formattedXml += '<?xml version="1.0" encoding="UTF-8"?>\n';
      
      // Format the document element
      formattedXml += prettyPrintXml(xmlDoc.documentElement);
      
      setXmlInput(formattedXml);
      setError('');
    } catch (e) {
      setError(`Error formatting XML: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert XML to JSON
  const convertXmlToJson = () => {
    setError('');
    setIsLoading(true);
    setIsJsonGenerated(false);
    setJsonOutput('');
    
    if (!xmlInput.trim()) {
      setError('Please enter XML data');
      setIsLoading(false);
      return;
    }

    try {
      // Convert XML to JSON
      const jsonData = xmlToJson(xmlInput);
      
      // Format JSON output
      const jsonString = prettyPrint 
        ? JSON.stringify(jsonData, null, indentLevel) 
        : JSON.stringify(jsonData);
      
      setJsonOutput(jsonString);
      setIsJsonGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting XML to JSON: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setXmlInput('');
    setJsonOutput('');
    setError('');
    setFileName('xml_data');
    setPrettyPrint(true);
    setIndentLevel(2);
    setPreserveAttributes(true);
    setArrayHandling('auto');
    setIsJsonGenerated(false);
  };

  // Copy JSON to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download JSON file
  const downloadJson = () => {
    const blob = new Blob([jsonOutput], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'xml_data'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">XML to JSON Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert XML data to JSON format. Paste your XML data below to generate a JSON file.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="xmlInput" className="font-medium text-gray-700">
                XML Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={formatXml}
                >
                  Format XML
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setXmlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="xmlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={xmlInput}
              onChange={handleInputChange}
              placeholder="Paste your XML data here, e.g.,
<?xml version='1.0' encoding='UTF-8'?>
<root>
  <person>
    <name>John</name>
    <age>30</age>
  </person>
  <person>
    <name>Jane</name>
    <age>28</age>
  </person>
</root>"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="prettyPrint"
                    checked={prettyPrint}
                    onChange={(e) => setPrettyPrint(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="prettyPrint" className="ml-2 block text-sm text-gray-700">
                    Pretty Print JSON
                  </label>
                </div>
                
                {prettyPrint && (
                  <div>
                    <label htmlFor="indentLevel" className="block text-sm font-medium text-gray-700 mb-1">
                      Indent Level
                    </label>
                    <select
                      id="indentLevel"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={indentLevel}
                      onChange={(e) => setIndentLevel(Number(e.target.value))}
                    >
                      <option value="1">1 Space</option>
                      <option value="2">2 Spaces</option>
                      <option value="4">4 Spaces</option>
                      <option value="8">8 Spaces</option>
                    </select>
                  </div>
                )}
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="preserveAttributes"
                    checked={preserveAttributes}
                    onChange={(e) => setPreserveAttributes(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="preserveAttributes" className="ml-2 block text-sm text-gray-700">
                    Preserve XML Attributes
                  </label>
                </div>
                
                <div>
                  <label htmlFor="arrayHandling" className="block text-sm font-medium text-gray-700 mb-1">
                    Array Detection
                  </label>
                  <select
                    id="arrayHandling"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={arrayHandling}
                    onChange={(e) => setArrayHandling(e.target.value)}
                  >
                    <option value="auto">Auto-detect Arrays</option>
                    <option value="always">Always Create Arrays</option>
                    <option value="never">Never Create Arrays</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertXmlToJson}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to JSON'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              JSON Output
            </label>
            
            {isJsonGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy JSON'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadJson}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isJsonGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    JSON output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isJsonGenerated && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="jsonOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={jsonOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isJsonGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">XML Successfully Converted to JSON!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated JSON for use in your applications.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About XML to JSON Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your XML data into JSON format. XML's hierarchical structure is preserved in the JSON output, 
              with elements becoming objects or arrays as appropriate. XML attributes are preserved as properties with the 
              "@attributes" prefix, and text content is handled intelligently.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Smart Conversion</strong> - Handles complex XML structures intelligently</li>
              <li><strong>Attribute Preservation</strong> - XML attributes are preserved in the JSON output</li>
              <li><strong>Array Detection</strong> - Automatically detects and converts repeated elements to arrays</li>
              <li><strong>Pretty Printing</strong> - Option to format JSON with customizable indentation</li>
              <li><strong>Error Handling</strong> - Clear error messages for invalid XML input</li>
              <li><strong>Easy Download</strong> - Save the converted JSON as a file with one click</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">XML to JSON Conversion Rules</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>XML elements with no children become JSON properties with string values</li>
              <li>XML elements with children become nested JSON objects</li>
              <li>Multiple XML elements with the same name are converted to JSON arrays</li>
              <li>XML attributes are preserved as properties with the "@attributes" prefix</li>
              <li>Text content of elements with attributes is stored as "#text" property</li>
              <li>Empty XML elements become null values in JSON</li>
              <li>XML comments are ignored in the conversion process</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format XML" button to check if your XML is valid before conversion</li>
              <li>XML is case-sensitive, so elements with different cases are treated as different elements</li>
              <li>For large XML files, consider disabling "Pretty Print" for better performance</li>
              <li>XML namespaces are preserved in the element names in the JSON output</li>
              <li>The structure of the JSON output closely follows the structure of the XML input</li>
              <li>Use the example button to see a properly formatted XML example</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XmlToJson;